import axios from "axios";

// --- Token storage (survives cross-site cookie blocking) ---
// When the frontend is served from staqblox.com but the backend lives on
// emergent.host, Chrome's 3rd-party cookie protections block the auth cookie.
// We fall back to localStorage + Authorization headers. Cookies are still sent
// in parallel (`withCredentials: true`) for same-site preview deployments.
const ACCESS_KEY = "sb_access_token";
const REFRESH_KEY = "sb_refresh_token";

export const tokens = {
  get access() { return localStorage.getItem(ACCESS_KEY) || ""; },
  get refresh() { return localStorage.getItem(REFRESH_KEY) || ""; },
  set(access, refresh) {
    if (access) localStorage.setItem(ACCESS_KEY, access);
    if (refresh) localStorage.setItem(REFRESH_KEY, refresh);
  },
  clear() {
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
  },
};

const API = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
  timeout: 60000, // 60s timeout — prevents infinite hang on LLM calls
});

// Attach Authorization: Bearer on every request if we have a stored access token.
API.interceptors.request.use((config) => {
  const t = tokens.access;
  if (t && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${t}`;
  }
  return config;
});

// On 401, try refresh once; if that succeeds, replay the original request.
let refreshInFlight = null;

API.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error.config || {};
    const status = error.response?.status;
    const url = original.url || "";
    const isAuthCall = url.includes("/auth/login") || url.includes("/auth/register") || url.includes("/auth/refresh");
    if (status !== 401 || original._retried || isAuthCall) {
      return Promise.reject(error);
    }
    original._retried = true;
    try {
      refreshInFlight = refreshInFlight || API.post("/auth/refresh", { refresh_token: tokens.refresh });
      const { data } = await refreshInFlight;
      if (data?.access_token) tokens.set(data.access_token, data.refresh_token);
      original.headers = original.headers || {};
      original.headers.Authorization = `Bearer ${tokens.access}`;
      return API(original);
    } catch (e) {
      tokens.clear();
      return Promise.reject(error);
    } finally {
      refreshInFlight = null;
    }
  },
);

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default API;
